import { Link } from "react-router-dom";
import { Gift, Sparkles, Copy, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useState } from "react";
import { toast } from "sonner";

export default function BonusCard({ bonus }) {
  const [copied, setCopied] = useState(false);
  const {
    casino_name,
    casino_logo,
    title,
    bonus_type,
    amount,
    free_spins,
    wagering,
    min_deposit,
    code,
    is_exclusive,
  } = bonus;

  const bonusTypeColors = {
    "welcome": "from-primary to-purple-500",
    "no-deposit": "from-green-500 to-emerald-400",
    "free-spins": "from-secondary to-amber-400",
    "reload": "from-accent to-blue-400",
  };

  const bonusTypeLabels = {
    "welcome": "Welcome Bonus",
    "no-deposit": "No Deposit",
    "free-spins": "Free Spins",
    "reload": "Reload Bonus",
  };

  const copyCode = () => {
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Bonus code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div 
      className="group relative rounded-2xl bg-card border border-white/5 overflow-hidden card-hover"
      data-testid={`bonus-card-${bonus.id}`}
    >
      {/* Type Badge */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${bonusTypeColors[bonus_type] || bonusTypeColors.welcome}`} />

      {is_exclusive && (
        <div className="absolute top-3 right-3 z-10">
          <Badge className="badge-exclusive flex items-center gap-1 px-2 py-1">
            <Sparkles className="w-3 h-3" /> Exclusive
          </Badge>
        </div>
      )}

      <div className="p-5">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-white/5 overflow-hidden flex-shrink-0 border border-white/10">
            <img 
              src={casino_logo} 
              alt={casino_name} 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1596838132731-3301c3fd4317?w=200";
              }}
            />
          </div>
          <div>
            <h3 className="text-white font-bold">{casino_name}</h3>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full bg-gradient-to-r ${bonusTypeColors[bonus_type] || bonusTypeColors.welcome} text-white`}>
              {bonusTypeLabels[bonus_type] || bonus_type}
            </span>
          </div>
        </div>

        {/* Bonus Amount */}
        <div className="text-center py-4 mb-4 rounded-xl bg-white/5">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Gift className="w-5 h-5 text-secondary" />
            <span className="text-2xl font-bold text-secondary">{amount}</span>
          </div>
          {free_spins > 0 && (
            <p className="text-white/60 text-sm">+ {free_spins} Free Spins</p>
          )}
        </div>

        {/* Details */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-white/50">Wagering:</span>
            <span className="text-white font-medium">{wagering}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-white/50">Min Deposit:</span>
            <span className="text-white font-medium">{min_deposit}</span>
          </div>
        </div>

        {/* Bonus Code */}
        {code && (
          <button
            onClick={copyCode}
            className="w-full flex items-center justify-between px-4 py-3 mb-4 rounded-lg bg-white/5 border border-dashed border-white/20 hover:border-primary transition-colors"
            data-testid={`copy-code-${bonus.id}`}
          >
            <span className="text-white/70 text-sm">Code:</span>
            <span className="text-primary font-mono font-bold">{code}</span>
            {copied ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4 text-white/40" />
            )}
          </button>
        )}

        {/* CTA */}
        <Button 
          className="w-full btn-glow gradient-neon text-white font-semibold rounded-full"
          data-testid={`claim-bonus-${bonus.id}`}
        >
          Claim Bonus
        </Button>
      </div>
    </div>
  );
}
